import { IDaySchedule, IPlanRange } from "@services/getMinerList";
import { parseDateFromFormat } from "./helpers";
import { isEmptyPeriods } from "./Periods.utils";

export interface IPeriodColor {
  readonly key: string; // ex: 00:00-01:59,03:00-05:59
  readonly color: string;
}

export function isEqualTemplate(
  periods: string[][],
  template: string[][],
): boolean {
  if (periods.length === template.length) {
    for (let i = 0; i < template.length; i++) {
      for (let j = 0; j < template[i].length; j++) {
        if (periods[i][j] !== template[i][j]) {
          return false;
        }
      }
    }
    return true;
  }
  return false;
}

export function periodsToPlanRange(
  periods: string[][]
): IPlanRange[] {
  return periods.map((period) => {
    return {
      time: period,
      active: true,
    } as IPlanRange;
  });
}

export function planRangeToPeriods(
  planRange: IPlanRange[]
): [string, string][] {
  return planRange.map((period) => {
    return period.time;
  });
};

export function getKeyForRange(range: IPlanRange[]): string {
  if (range.length === 0) throw Error("Empty range");
  return range.map(r => r.time.join('-')).join(',')
}

export function getKeyForPeriods(range: string[][]): string {
  if (range.length === 0) throw Error("Empty range");
  return range.map(r => r.join('-')).join(',')
}

function getColorDistance(colorA: string, colorB: string): number {
  const r1 = parseInt(colorA.slice(1, 3), 16);
  const g1 = parseInt(colorA.slice(3, 5), 16);
  const b1 = parseInt(colorA.slice(5, 7), 16);

  const r2 = parseInt(colorB.slice(1, 3), 16);
  const g2 = parseInt(colorB.slice(3, 5), 16);
  const b2 = parseInt(colorB.slice(5, 7), 16);

  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

export function getColorForRange(
  period: IPlanRange[],
  periodsColors: IPeriodColor[]
): IPeriodColor {
  if (period.length === 0) {
    return {
      key: "",
      color: "",
    }
  }
  for (let i = 0; i < periodsColors.length; i++) {
    if (getKeyForRange(period) === periodsColors[i].key) {
      return periodsColors[i];
    }
  }
  return {
    key: "new",
    color: createColorWithCheck(periodsColors.map(pc => pc.color)),
  }
};

function getRandomComponent() {
  return Math.floor(Math.random() * 256);
}

function generateRandomColor(): string {
  const r = getRandomComponent().toString(16).padStart(2, '0');
  const g = getRandomComponent().toString(16).padStart(2, '0');
  const b = getRandomComponent().toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export function generateRandomColorEx(
  excluded: string,
  threshold: number = 100
): string {
  let newColor;
  do {
    const r = getRandomComponent().toString(16).padStart(2, '0');
    const g = getRandomComponent().toString(16).padStart(2, '0');
    const b = getRandomComponent().toString(16).padStart(2, '0');
    newColor = `#${r}${g}${b}`;
  } while (getColorDistance(newColor, excluded) < threshold);
  return newColor;
}

function isColorsDistanceOk(
  color: string,
  colors: string[],
  threshold: number = 100
) {
  for (let i = 0; i < colors.length; i++) {
    if (getColorDistance(color, colors[i]) < threshold) {
      return false;
    }
  }
  return true;
}

export function createColorWithCheck(colors: string[]): string {
  let limit = 0;
  let color = generateRandomColor();
  while (!isColorsDistanceOk(color, colors)) {
    limit++;
    color = generateRandomColor();
    if (limit >= 10000) break;
  }
  return color;
}

export function clearEmptyPeriodsWithoutWeekDayPlan(
  schedule: IDaySchedule[],
  //crossedDays: IDaySchedule[],
): IDaySchedule[] {
  const idxForRemove: number[] = [];
  for (let i = 0; i < schedule.length; i++) {
    if (isEmptyPeriods(schedule[i].range) && schedule[i].day.length > 1) {
      try {
        const weekDay = parseDateFromFormat(schedule[i].day).getDay().toString();
        const weekDayPlan = schedule.find(sch => sch.day === weekDay);
        //const crossedPlan = crossedDays.find(cd => cd.day === weekDay);
        //if (!crossedPlan || crossedPlan.range.length === 0) {
        if (!weekDayPlan || weekDayPlan.range.length === 0) {
          idxForRemove.push(i);
        }
        //}
      } catch { }
    }
  }
  return schedule.filter((sch, i) => idxForRemove.indexOf(i) === -1);
}

export function getNextDay(day: string): string {
  if (day.length === 1) {
    const weekDay = parseInt(day);
    if (weekDay >= 0 && weekDay <= 6) {
      return ((weekDay + 1) % 7).toString();
    }
    throw new Error("Incorrect day of the week format. Must be a number from 0 to 6."); //'Неверный формат дня недели. Должно быть число от 0 до 6');
  }
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(day)) {
    throw new Error("Incorrect date format. Expected: DD/MM/YYYY."); //'Неверный формат даты. Ожидается DD/MM/YYYY');
  }
  const [dd, mm, yyyy] = day.split('/').map(Number);
  const date = new Date(yyyy, mm - 1, dd);
  date.setDate(date.getDate() + 1);
  const nextDay = date.getDate().toString().padStart(2, '0');
  const nextMonth = (date.getMonth() + 1).toString().padStart(2, '0');
  const nextYear = date.getFullYear();
  return `${nextDay}/${nextMonth}/${nextYear}`;
}